import axios from 'axios';
import { escapeRegExp } from 'lodash';

import { FieldName as LocationFieldName } from 'admin/locations/Locations';
import { FieldName as RegionFieldName } from 'admin/regions/Regions';
import { Profile } from 'auth/Auth';
import { LocaleCode } from 'core/util/Locale';
import { Logical, Operator, SearchRequest, SortOrder } from 'core/util/Search';
import {
    AddressTemplates,
    Country,
    Location,
    LogResponse,
    Normalize,
    Region,
    Ticket,
    TicketCustomDefinitions,
    User,
} from 'tickets/actions2/common/action.types';
import { UserFieldName } from 'tickets/actions2/common/ticket-constants';

export interface Queue {
    name: string;
    id: number | string;
}
export interface Stage {
    name: string;
    id: number | string;
    order: number;
    onHold: boolean;
}
export const saveContact = async (agencyId: number, tenantId: number, contact: any) => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/tickets/saveContact`, { agencyId, tenantId, contact });

    return data;
};

export const fetchContacts = async (tenantId: number, search: string, args?: string) => {
    const { data } = await axios.get(`/api/tenants/${tenantId}/tickets/contacts`, {
        params: { tenantId, search, args },
    });

    return data;
};

export const removeContact = async (tenantId: number, contactId: number) => {
    const { data } = await axios.delete(`/api/tenants/${tenantId}/tickets/contacts/${contactId}`);

    return data;
};

export const saveTicket = async (agencyId: number, tenantId: number, ticket: any, args: any) => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/tickets`, {
        agencyId,
        tenantId,
        ticket,
        args,
    });

    return data;
};

export const getQueues = async (tenantId: number, search: SearchRequest, args: any): Promise<Queue[]> => {
    const { data } = await axios.get(`/api/tenants/${tenantId}/queues`, {
        params: { tenantId, search, args },
    });

    return data;
};

export const saveNote = async (agencyId: number, tenantId: number, ticketNote: any) => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/ticket-notes`, {
        agencyId,
        tenantId,
        ticketNote,
    });

    return data;
};

export const getStages = async (tenantId: number, search: SearchRequest, args: any): Promise<Stage[]> => {
    const { data } = await axios.get(`/api/tenants/${tenantId}/stages`, {
        params: { tenantId, search, args },
    });

    return data;
};

export interface Tag {
    name: string;
    id: number | string;
    agencyID: number;
    system: boolean;
    tenantID: number;
    ticketDefinitionIDs: string[];
}

export const getTags = async (tenantId: number, search: SearchRequest, args: any): Promise<Tag[]> => {
    const { data } = await axios.get(`/api/tenants/${tenantId}/tags`, {
        params: { tenantId, search, args },
    });

    return data;
};
export interface RootCause {
    id: number | string;
    isHidden: boolean;
    rootCause: string;
    ticketDefinitionIDs?: number | string;
}

export const getRootCauses = async (tenantId: number, search: SearchRequest, args: any): Promise<RootCause[]> => {
    const { data } = await axios.get(`/api/tenants/${tenantId}/root-causes`, {
        params: { tenantId, search, args },
    });

    return data;
};

export const bulkSaveTickets = async (tenantId: number, tickets: any, args: any = {}) => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/tickets/bulksavetickets`, { tickets, args });

    return data;
};

export const getLogs = async (tenantId: number, ticketId: number): Promise<LogResponse> => {
    const { data } = await axios.get<LogResponse>(`/api/tenants/${tenantId}/tickets/${ticketId}/logs`);

    return data;
};

export const getSingleTicket = async (tenantId: number, ticketId: number): Promise<any> => {
    const params = {
        args: {
            includeMacros: true,
            includeAllSentimentMacros: false,
            includeCredentials: false,
            includeReviewResponses: true,
            includeFlags: true,
            includePageSummaries: false,
            includeUsers: true,
            includeReview: false,
            includeSurveyTemplates: true,
            isExternalView: false,
            includeHistoryCounts: true,
            isActionPage: true,
        },
    };
    const { data } = await axios.get<any>(`/api/tenants/${tenantId}/tickets/${ticketId}`, { params });

    return data;
};

export const setPublished = async (tenantId: number, reviewId: string, args: any = {}) => {
    const { data } = await axios.put(`/api/tenants/${tenantId}/reviews/${reviewId}/set-published`, {
        tenantId,
        reviewId,
        args,
    });

    return data;
};

export const getUsersByLocationUid = async (agencyId: number, tenantId: number, args: any = {}) => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/users/search-by-location-uids`, {
        tenantId,
        agencyId,
        ...args,
    });

    return data;
};

export const forwardTicket = async (tenantId: number, ticketId: string, args: any = {}) => {
    const { data } = await axios.put(`/api/tenants/${tenantId}/tickets/${ticketId}/forward`, {
        tenantId,
        args,
    });

    return data;
};

export const fetchTicketTypeConfigurations = async (tenantId: number, search: SearchRequest, args?: any) => {
    const { data } = await axios.get(`/api/tenants/${tenantId}/ticket-types/configurations`, {
        params: { tenantId, search, args },
    });

    return data;
};

export const updateBulkAssignees = async (tenantId: number, args: any = {}) => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/tickets/update-tickets-assignee`, {
        tenantId,
        ...args,
    });

    return data;
};

export const countTickets = async (agencyId: number, tenantId: number, search: SearchRequest, args: any = {}) => {
    const params = { agencyId, tenantId, search, args };
    const { data } = await axios.put(`/api/tenants/${tenantId}/tickets/count`, params);

    return data;
};

export const getSingleTicketDefinitionDetails = async (
    tenantId: number,
    ticketDefinitionID: string,
): Promise<TicketCustomDefinitions> => {
    const { data } = await axios.get<TicketCustomDefinitions>(
        `/api/tenants/${tenantId}/ticket-custom-definitions/types/${ticketDefinitionID}/details`,
    );

    return data;
};

export const getAllTicketDefinitionsDetails = async (tenantId: number): Promise<TicketCustomDefinitions[]> => {
    const { data } = await axios.get<TicketCustomDefinitions[]>(
        `/api/tenants/${tenantId}/ticket-custom-definitions/types/alldetails`,
    );

    return data;
};

export const saveCustomDefinitions = async (
    tenantId: number,
    ticketId: number,
    args: any,
): Promise<Pick<Ticket, 'customFields'>> => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/tickets/${ticketId}/custom-definitions`, {
        ...args,
    });

    return data;
};

export const getAddressTemplates = async (country: string, locale: LocaleCode): Promise<AddressTemplates> => {
    const params = {
        country,
        args: { key: `${country}-${locale}` },
    };
    const { data } = await axios.get<AddressTemplates>('/api/address/template', { params });

    return data;
};

export const getCountries = async (profile: Profile | undefined): Promise<Country[]> => {
    const params = {
        args: {
            key: `${profile?.user?.id}-${profile?.user?.locale}`,
        },
    };
    const { data } = await axios.get<Country[]>('/api/countries', { params });

    return data;
};

export const getCountryRegions = async (userId: number, countryId: string, locale: LocaleCode): Promise<Region[]> => {
    const params = {
        search: { filters: [{ name: RegionFieldName.CountryId, values: [countryId] }] },
        args: { key: `${userId}-${locale}` },
    };
    const { data } = await axios.get<Region[]>('/api/regions/', { params });

    return data;
};

export const searchLocation = async (searchStr: string, tenantId: number): Promise<Location[]> => {
    const params = {
        search: {
            filters: [{ name: LocationFieldName.Search, values: [escapeRegExp(searchStr)], operator: Operator.iLike }],
            projections: [LocationFieldName.InternalName, LocationFieldName.Uid],
        },
    };

    const { data } = await axios.get<Location[]>(`/api/tenants/${tenantId}/r4e-locations`, { params });

    return data;
};

export const getAllLocations = async (tenantId: number): Promise<Location[]> => {
    const params = {
        search: {
            projections: [
                LocationFieldName.Id,
                LocationFieldName.InternalName,
                LocationFieldName.Uid,
                LocationFieldName.Code,
            ],
        },
    };

    const { data } = await axios.get<Location[]>(`/api/tenants/${tenantId}/r4e-locations`, { params });

    return data;
};

export const searchUser = async ({
    searchStr,
    locationUid,
    tenantId,
    agencyId,
}: {
    searchStr: string;
    locationUid: number;
    tenantId: number;
    agencyId: number;
}): Promise<User[]> => {
    const params = {
        agencyId,
        search: {
            filters: [
                { name: UserFieldName.LocationUids, values: [locationUid.toString()] },
                { name: UserFieldName.System, values: ['false'] },
                { name: UserFieldName.Active, values: ['true'] },
                { name: UserFieldName.DisplayName, values: [searchStr], operator: Operator.iLike },
                { name: UserFieldName.Email, values: [searchStr], operator: Operator.iLike, logical: Logical.Or },
            ],
            sorts: [{ name: UserFieldName.DisplayName, sortOrder: SortOrder.Asc }],
            projections: [UserFieldName.DisplayName, UserFieldName.LocationUids, UserFieldName.Email],
        },
    };

    const { data } = await axios.get<User[]>(
        `${window.location.protocol}//${window.location.host}/api/tenants/${tenantId}/users`,
        { params },
    );

    return data;
};

export const normalize = async (address: {
    country: string;
    locality: string | null;
    postalCode: string;
    streetAddress: string;
    streetAddress2: string;
    region: string | null;
}): Promise<Normalize> => {
    const { data } = await axios.put<Normalize>(
        `${window.location.protocol}//${window.location.host}/api/address/normalize`,
        { address },
    );

    return data;
};

export const matchTickets = async ({
    tenantId,
    phoneNumber,
    originalTicketId,
    email,
}: {
    tenantId: number;
    email: string;
    originalTicketId?: number;
    phoneNumber: string;
}): Promise<Ticket[]> => {
    const params = { search: { phoneNumber, email, originalTicketId } };
    const { data } = await axios.get<Ticket[]>(
        `${window.location.protocol}//${window.location.host}/api/tenants/${tenantId}/tickets/matchingTickets`,
        { params },
    );

    return data;
};

export const matchAgencyTickets = async ({
    objectId,
    ticketType,
    tenantId = -1,
    originalTicketId,
}: {
    objectId: string;
    ticketType: string;
    tenantId?: number;
    originalTicketId?: number;
}): Promise<Ticket[]> => {
    const params = { search: { objectId, ticketType, originalTicketId } };
    const { data } = await axios.get<Ticket[]>(`/api/tenants/${tenantId}/tickets/matchingAgencyTickets`, { params });

    return data;
};

export const createTicket = async (ticket: Ticket, tenantId: number, agencyId: number): Promise<Ticket> => {
    const payload = {
        agencyId,
        tenantId,
        ticket,
        args: { skipCustomFieldValidation: true },
    };

    const { data } = await axios.post<Ticket>(
        `${window.location.protocol}//${window.location.host}/api/tenants/${tenantId}/tickets`,
        payload,
    );

    return data;
};
export const cloneTicket = async (
    ticket: Ticket,
    tenantId: number,
    agencyId: number,
    parentId: number,
): Promise<Ticket> => {
    const payload = {
        agencyId,
        tenantId,
        parentId,
        ticket,
    };

    const { data } = await axios.post<Ticket>(`/api/tenants/${tenantId}/tickets/clone-ticket`, payload);

    return data;
};

export const getNotes = async (tenantId: number, args: any, filters: any): Promise<any> => {
    const params = {
        args,
        search: {
            filters: JSON.parse(filters),
        },
    };
    const { data } = await axios.get(`/api/tenants/${tenantId}/ticket-notes`, {
        params,
    });

    return data;
};

export const deleteNotes = async (tenantId: number, noteId: number, agencyId: any): Promise<any> => {
    const params = {
        agencyId,
    };
    const { data } = await axios.delete(`/api/tenants/${tenantId}/ticket-notes/${noteId}`, {
        params,
    });

    return data;
};

export const addNotes = async (tenantId: number, agencyId: number, ticketNote: any): Promise<any> => {
    const payload = {
        agencyId,
        tenantId,
        ticketNote,
    };
    const { data } = await axios.post(`/api/tenants/${tenantId}/ticket-notes`, payload);

    return data;
};

export interface ClosingReason {
    id: number | string;
    isHidden: boolean;
    reason: string;
    tenantID: number;
    ticketDefinitionIDs?: string[];
}

export const getClosingReasons = async (tenantId: number): Promise<ClosingReason[]> => {
    const { data } = await axios.get(`/api/tenants/${tenantId}/closing-reason`);

    return data;
};

export interface UserPreferences {
    viewType: string;
    pageSize: number;
}

export const getUserPreferences = async (tenantId: number): Promise<UserPreferences> => {
    const { data } = await axios.get(`/api/tenants/${tenantId}/tickets/get-ticket-user-preferences`);

    return data;
};
